import { EventEmitter } from 'events'
import { EStatus } from '../utils/async'
import { Base_Dispatcher } from '../../zap/Base/Base.Dispatcher'
import { SD_equal } from '../../zap/SD/SD.equal'
import type { Type_ISmartDataOrdering, Type_ISmartDataResult } from '../../zap/Type/Type.hive'
import {
    SD_ADD,
    SD_DELETE,
    SD_EDIT,
    SD_SET_DETAILS,
    SD_SET_ORDERING,
    SD_SET_RESULTS,
    SD_SET_STATUS,
} from '../../zap/SD/SD.constants'

const CHANGE_EVENT = 'change'

let _details: any = []
let _data: Type_ISmartDataResult[] = []
let _ordering: Type_ISmartDataOrdering[] = []
let _status: EStatus = EStatus.loading

class SmartDataStore extends EventEmitter {
    public setDetails(data: any) {
        _details = data
    }

    public getDetails() {
        return _details
    }

    public setData(data: any) {
        _data = data
    }

    public getData() {
        return _data
    }

    public setStatus(st: EStatus) {
        _status = st
    }

    public getStatus() {
        return _status
    }

    public setOrdering(ordering: Type_ISmartDataOrdering[]) {
        _ordering = ordering
    }

    public getOrdering() {
        return _ordering
    }

    public add(target: any, data: any) {
        // Find the entry with the same target spec and add to that
        let found = false
        for (const entry of _data) {
            if (SD_equal(target, entry.spec)) {
                entry.data.push(data)
                found = true
            }
        }

        if (!found) {
            _data.push({
                title: undefined,
                spec: target,
                data: [data],
            })
        }

        // Refresh data pointer so that results re-display
        // This could be removed if we moved to immutable values
        _data = [].concat(_data)
    }

    public edit(id: any, data: any) {
        /* This is all pretty ugly. We seach through the data to find the right entry and then
           update it in place followed by refreshing the pointer below so that shallow equality
           checks know that it has changed */
        for (const entry of _data) {
            for (let i = 0; i < entry.data.length; ++i) {
                if (entry.data[i].id === id) {
                    // Merge data into appropriate smart data entry
                    entry.data[i] = { ...entry.data[i], ...data }
                }
            }
        }

        // Refresh data pointer so that results re-display
        // This could be removed if we moved to immutable values
        _data = [].concat(_data)
    }

    public delete_(id: any) {
        // Find and remove entry from smart data - we don't attempt to remove any title/spec section
        for (const entry of _data) {
            for (let i = 0; i < entry.data.length; ++i) {
                if (entry.data[i].id === id) {
                    entry.data.splice(i, 1)
                }
            }
        }

        // Refresh data pointer so that results re-display
        // This could be removed if we moved to immutable values
        _data = [].concat(_data)
    }

    public emitChange() {
        this.emit(CHANGE_EVENT)
    }

    public addChangeListener(callback: any) {
        this.on(CHANGE_EVENT, callback)
    }

    public removeChangeListener(callback: any) {
        this.removeListener(CHANGE_EVENT, callback)
    }
}

const store = new SmartDataStore()

// Register callback to handle all updates
Base_Dispatcher.register(function (action: any) {
    if (action.actionType === undefined) {
        throw Error('Attempting to dispatch an undefined action. Please check your constants.')
    }

    switch (action.actionType) {
        case SD_SET_DETAILS:
            store.setDetails(action.payload)
            break

        case SD_SET_RESULTS:
            store.setData(action.data)
            store.emitChange()
            break

        case SD_SET_ORDERING:
            store.setOrdering(action.data)
            store.emitChange()
            break

        case SD_SET_STATUS:
            store.setStatus(action.data)
            store.emitChange()
            break

        case SD_EDIT:
            store.edit(action.data.id, action.data.data)
            store.emitChange()
            break

        case SD_ADD:
            store.add(action.data.target, action.data.data)
            store.emitChange()
            break

        case SD_DELETE:
            store.delete_(action.data)
            store.emitChange()
            break

        default:
            return true
    }
})

export default store
